<template>
    <div>
        <div class="form-group col-12">
            <div
                class="row mb-5 mr-2 d-flex justify-content-between align-items-center"
            >
                <div class="col-12 col-xl-6 form-group">
                    <label for="userName">
                        User name
                    </label>
                    <input
                        id="userName"
                        :value="file.user.fullName"
                        class="form-control"
                        disabled
                        type="text"
                    />
                </div>

                <div class="col-4 form-group">
                    <label for="status">
                        Status
                    </label>
                    <b-badge
                        pill
                        class="my-1 px-3 form-control d-flex align-items-center  justify-content-center"
                        :variant="statusColor"
                    >
                        {{ file.status.toUpperCase() }}
                    </b-badge>
                </div>

                <div>
                    <a
                        :href="fileLink"
                        class="btn btn-info btn-static waves-effect waves-light m-1"
                    >
                        <i class="fas fa-download" />
                        Download
                    </a>
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-xl-6 form-group">
                    <label for="name">
                        Name
                    </label>
                    <input
                        id="name"
                        :value="file.media.name"
                        class="form-control"
                        disabled
                        type="text"
                    />
                </div>
                <div class="col-12 col-xl-3 form-group">
                    <label for="fileType">
                        Category
                    </label>
                    <v-select
                        id="fileType"
                        v-model="file.fileType"
                        :options="fileTypeOptions"
                        :reduce="fileType => fileType.value"
                        label="text"
                        :class="{
                            'is-invalid': hasError('fileType')
                        }"
                        :clearable="false"
                        :searchable="false"
                    />
                    <div class="invalid-feedback">
                        {{ getError('fileType') }}
                    </div>
                </div>
                <div class="col-12 col-xl-3 form-group">
                    <label for="type">
                        File type
                    </label>
                    <v-select
                        id="type"
                        v-model="file.type"
                        :options="typeOptions"
                        :class="{
                            'is-invalid': hasError('type')
                        }"
                        :clearable="false"
                        :searchable="false"
                    />
                    <div class="invalid-feedback">
                        {{ getError('type') }}
                    </div>
                </div>

                <div class="col-12 col-xl-3 form-group">
                    <label for="fileId">
                        File ID
                    </label>
                    <input
                        id="fileId"
                        :value="file.number"
                        class="form-control"
                        disabled
                        type="number"
                    />
                </div>
                <div class="col-12 col-xl-3 form-group">
                    <label for="campusName">
                        Campus
                    </label>
                    <input
                        id="campusName"
                        :value="campusName"
                        class="form-control"
                        disabled
                        type="text"
                    />
                </div>
                <div class="col-12 col-xl-3 form-group">
                    <label for="year">
                        Year
                    </label>
                    <v-select
                        id="year"
                        v-model="file.year"
                        :options="yearOptions"
                        :class="{
                            'is-invalid': hasError('year')
                        }"
                        :clearable="false"
                        :searchable="false"
                    />
                    <div class="invalid-feedback">
                        {{ getError('year') }}
                    </div>
                </div>
                <div class="col-12 col-xl-3 form-group">
                    <label for="term">
                        Moed
                    </label>
                    <v-select
                        id="term"
                        v-model="file.term"
                        :options="termOptions"
                        :class="{
                            'is-invalid': hasError('term')
                        }"
                        :clearable="false"
                        :searchable="false"
                    />
                    <div class="invalid-feedback">
                        {{ getError('term') }}
                    </div>
                </div>

                <div class="col-12 col-xl-6 form-group">
                    <label for="likes">
                        Likes
                    </label>
                    <input
                        id="likes"
                        :value="file.likes"
                        class="form-control"
                        disabled
                        type="number"
                    />
                </div>
                <div class="col-12 col-xl-6 form-group">
                    <label for="isReported">
                        Is Reported
                    </label>
                    <b-badge
                        pill
                        class="my-1 px-3 form-control d-flex align-items-center  justify-content-center"
                        :variant="file.isReported ? 'danger' : 'success'"
                    >
                        {{ isReported }}
                    </b-badge>
                    <div
                        v-if="file.isReported"
                        style="cursor: pointer"
                        class="text-center"
                        @click="$bvModal.show('reports-modal')"
                    >
                        Show reports
                    </div>
                </div>
            </div>
        </div>

        <reports-modal :id="file.id" type="files" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReportsModal from '@/components/reported-content/ReportsModal';

export default {
    components: {
        ReportsModal
    },

    props: {
        value: {
            type: Object,
            required: true
        },
        validation: {
            type: Object,
            required: true
        },
        serverErrors: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            file: {}
        };
    },

    computed: {
        ...mapGetters({
            fileTypeOptions: 'files/FILE_TYPE_OPTIONS',
            termOptions: 'files/TERM_OPTIONS',
            typeOptions: 'files/TYPE_OPTIONS',
            PENDING: 'files/STATUS_PENDING',
            APPROVED: 'files/STATUS_APPROVED',
            REJECTED: 'files/STATUS_REJECTED'
        }),

        isReported() {
            return this.file.isReported ? 'YES' : 'NO';
        },

        fileLink() {
            return this.$getFileUrl(this.file.media);
        },

        campusName() {
            const [campus] = this.file.user.campuses;

            return campus?.name || '-';
        },

        yearOptions() {
            let from = 2012;
            const to = this.$dayjs().year();

            const yearsOptions = [];

            while (to >= from) {
                yearsOptions.push(from);

                from++;
            }

            return yearsOptions;
        },

        statusColor() {
            const { status } = this.file;

            if (status === this.APPROVED) {
                return 'success';
            }

            if (status === this.REJECTED) {
                return 'danger';
            }

            return 'secondary';
        }
    },

    watch: {
        file() {
            this.$emit('input', this.file);
        }
    },

    created() {
        this.file = { ...this.value };
    },

    methods: {
        hasError(key) {
            return (
                this.validation.file[key].$error ||
                this.serverErrors.find(error => error.param === key)
            );
        },

        getError(key) {
            return this.$getValidationErrorMessageHandler.handle(
                this.validation.file,
                this.serverErrors,
                key
            );
        }
    }
};
</script>
